import { newId } from '@app/shared';
import { db, type OutboxEntry } from '../db.ts';
import { clientNow } from './clock.ts';

/**
 * Queue a local change for synchronization. Runs inside the caller's Dexie
 * transaction when there is one, so the optimistic record write and the queued
 * mutation commit together.
 *
 * Coalescing keeps the queue small while offline: successive edits to the same
 * record merge into one pending mutation (same id, same base revision), and
 * deleting something that was never synced simply drops it.
 */
export async function enqueue(entry: Pick<OutboxEntry, 'familyId' | 'collection' | 'recordId' | 'op' | 'patch' | 'baseRev'>): Promise<void> {
  const forRecord = await db.outbox.where('[collection+recordId]').equals([entry.collection, entry.recordId]).toArray();
  const queued = forRecord.filter((e) => e.state === 'queued').sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const sending = forRecord.some((e) => e.state === 'sending');

  if (entry.op === 'upsert') {
    const last = queued[queued.length - 1];
    if (last && last.op === 'upsert') {
      await db.outbox.update(last.seq!, { patch: { ...last.patch, ...entry.patch }, clientTime: clientNow() });
      return;
    }
  }
  if (entry.op === 'delete') {
    const neverSynced = !sending && queued.some((e) => e.op === 'upsert' && e.baseRev === 0);
    if (queued.length) await db.outbox.bulkDelete(queued.filter((e) => e.op === 'upsert').map((e) => e.seq!));
    if (neverSynced) return;
  }
  await db.outbox.add({
    ...entry,
    mid: newId(),
    clientTime: clientNow(),
    state: 'queued',
    attempts: 0,
  });
}

/** Patches still waiting to be sent for a record, merged in order. */
export async function pendingPatch(collection: string, recordId: string): Promise<{ patch: Record<string, unknown>; deleting: boolean } | null> {
  const entries = await db.outbox.where('[collection+recordId]').equals([collection, recordId]).sortBy('seq');
  if (entries.length === 0) return null;
  const patch: Record<string, unknown> = {};
  let deleting = false;
  for (const e of entries) {
    if (e.op === 'upsert') Object.assign(patch, e.patch);
    if (e.op === 'delete') deleting = true;
    if (e.op === 'restore') deleting = false;
  }
  return { patch, deleting };
}
